import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ApiError, api } from '@/lib/api';
import Icon from '@/components/Icon';

/**
 * Страница установки нового пароля по одноразовой ссылке из письма.
 * Открывается без аутентификации.
 *
 * Шаги:
 *   • При входе проверяем токен GET /api/auth/password-reset/check.
 *     Если он невалиден / истёк / уже использован — показываем
 *     соответствующее сообщение и ссылку «Запросить новую».
 *   • Если валиден — рисуем два поля пароля и кнопку «Сохранить».
 *   • POST /api/auth/password-reset/confirm — меняет пароль; после
 *     успеха показываем «Готово, можно войти» с ссылкой на /login.
 *
 * Стилизуется через те же .login-* классы, что и страница входа —
 * формы выглядят однородно (один логотип, центрированная карточка).
 */
export default function PasswordResetConfirmPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const token = search.get('token') || '';

  // Состояние проверки токена: null = ещё не проверили, иначе ответ бэка.
  const [check, setCheck] = useState(null);
  const [checkError, setCheckError] = useState(null);

  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setCheckError('В ссылке нет токена. Запросите письмо ещё раз.');
      return;
    }
    api.get('/api/auth/password-reset/check?token=' + encodeURIComponent(token))
      .then(setCheck)
      .catch((e) => setCheckError(e instanceof ApiError ? e.message : 'Не удалось проверить ссылку'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8)         { setError('Пароль должен быть не короче 8 символов'); return; }
    if (password !== confirm)        { setError('Пароли не совпадают'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/api/auth/password-reset/confirm', {
        token, password, passwordConfirm: confirm,
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить пароль');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <img src="/img/np-logo.png" alt="Новое Поколение" className="login-logo__img" />
        </div>
        <h1 className="login-title">Новый пароль</h1>

        {/* Проверка токена */}
        {!done && checkError && (
          <div className="login-form">
            <div className="login-error-banner" role="alert">
              <Icon name="alert" size={14} />
              <span>{checkError}</span>
            </div>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Link className="login-forgot" to="/login">Вернуться ко входу</Link>
            </div>
          </div>
        )}

        {!done && !checkError && !check && (
          <div className="login-form" style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>
            Проверяем ссылку…
          </div>
        )}

        {!done && check && !check.ok && (
          <div className="login-form">
            <div className="login-error-banner" role="alert">
              <Icon name="alert" size={14} />
              <span>
                {check.reason === 'expired' && 'Ссылка устарела. Запросите новую.'}
                {check.reason === 'used'    && 'По этой ссылке уже сменили пароль. Если это были не вы — запросите новую.'}
                {check.reason === 'invalid' && 'Ссылка не распознана. Возможно, она была испорчена при копировании.'}
                {!['expired','used','invalid'].includes(check.reason) && 'Ссылка недействительна.'}
              </span>
            </div>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Link className="login-forgot" to="/login">К форме входа</Link>
            </div>
          </div>
        )}

        {/* Форма установки пароля */}
        {!done && check?.ok && (
          <form className="login-form" onSubmit={submit} noValidate>
            <div className="login-field">
              <label className="login-field__label">Пользователь</label>
              <div className="login-field__input" style={{ background: 'var(--panel-2)', display: 'flex', alignItems: 'center' }}>
                {check.userName || '—'}
              </div>
            </div>

            {error && (
              <div className="login-error-banner" role="alert">
                <Icon name="alert" size={14} />
                <span>{error}</span>
              </div>
            )}

            <div className="login-field">
              <label className="login-field__label" htmlFor="pwd-new">Новый пароль</label>
              <div className="login-field__password">
                <input
                  id="pwd-new"
                  className={`login-field__input ${error ? 'login-field__input--error' : ''}`}
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="new-password"
                  minLength={8}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (error) setError(null); }}
                  placeholder="Минимум 8 символов"
                  autoFocus
                />
                <button
                  type="button"
                  className="login-field__toggle"
                  onClick={() => setShowPwd((s) => !s)}
                  aria-label={showPwd ? 'Скрыть пароль' : 'Показать пароль'}
                  tabIndex={-1}
                >
                  <Icon name={showPwd ? 'eye-off' : 'eye'} size={16} />
                </button>
              </div>
            </div>

            <div className="login-field">
              <label className="login-field__label" htmlFor="pwd-confirm">Повторите пароль</label>
              <input
                id="pwd-confirm"
                className={`login-field__input ${error ? 'login-field__input--error' : ''}`}
                type={showPwd ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value); if (error) setError(null); }}
              />
            </div>

            <button type="submit" className="login-submit" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="login-submit__spinner" />
                  <span>Сохраняем…</span>
                </>
              ) : (
                'Сохранить пароль'
              )}
            </button>
          </form>
        )}

        {done && (
          <div className="login-form">
            <div
              className="lf-success"
              style={{ textAlign: 'center', padding: '16px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
            >
              <div className="lf-success__icon"><Icon name="check" size={22} /></div>
              <p className="lf-success__title">Пароль обновлён</p>
              <p className="lf-success__text">Теперь можно войти в систему с новым паролем.</p>
            </div>
            <button
              type="button"
              className="login-submit"
              onClick={() => navigate('/login')}
            >
              Перейти ко входу
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
